import React, { useEffect, useMemo, useState, useRef } from 'react'
import { Helmet } from 'react-helmet'
import { Switch, Route } from 'react-router-dom'
import { NavTabs } from '@abroad/components'
import { NavLink, useLocation, useHistory } from 'react-router-dom'
import {
  useErrorService,
  useNotificationService,
  ABSpinner,
} from '@abroad/components'
import { PageHeader } from '../components/common'
import {
  BookingTab,
  SettingTab,
  CalendarTab,
  ZoomTab,
  TimezoneModal,
} from '../components/scheduling'
import { SelectionField } from '../components/common'
import API from '../utils/API'
import { getSortedTimezones } from '../utils/timezone'

const Scheduling = () => {
  const location = useLocation()
  const history = useHistory()
  const Error = useErrorService()
  const Notification = useNotificationService()
  const [tabRefreshKey, setTabRefreshKey] = useState(0)

  const [selectedTimezone, setSelectedTimezone] = useState('')
  const [isCalendarConnected, setIsCalendarConnected] = useState(false)
  const [showTimezoneModal, setShowTimezoneModal] = useState(false)
  const [isSavingTimezone, setIsSavingTimezone] = useState(false)
  const [isModalDismissed, setIsModalDismissed] = useState(false)
  const [isLoadingTimezone, setIsLoadingTimezone] = useState(true)
  const hasFetchedTimezone = useRef(false)

  // Prepare timezone options - get all timezones from moment-timezone
  const timezoneOptions = useMemo(() => {
    return getSortedTimezones()
  }, [])

  // Fetch timezone on mount (only once)
  useEffect(() => {
    // Only fetch once on initial mount
    if (hasFetchedTimezone.current) {
      return
    }

    const fetchTimezone = async () => {
      try {
        hasFetchedTimezone.current = true
        setIsLoadingTimezone(true)
        const { data } = await API.CoachAPI.coachUser.getTimezone()
        const timezone = data?.timezone || ''
        const calendarConnected = data?.isAbroadCalendarConnected || false

        setSelectedTimezone(timezone)
        setIsCalendarConnected(calendarConnected)

        // Show modal if timezone is empty and modal hasn't been dismissed
        if (!timezone && !isModalDismissed) {
          setShowTimezoneModal(true)
        }
      } catch (error) {
        Error.showError(error)
        // Show modal on error as well to allow user to set timezone
        if (!isModalDismissed) {
          setShowTimezoneModal(true)
        }
      } finally {
        setIsLoadingTimezone(false)
      }
    }

    fetchTimezone()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Show modal again when navigating to tabs if timezone is not set
  useEffect(() => {
    // Only check after initial timezone fetch is complete
    if (isLoadingTimezone) {
      return
    }

    // Check if we're on any scheduling tab
    const isSchedulingTab =
      location.pathname === '/coach/scheduling' ||
      location.pathname === '/coach/scheduling/booking' ||
      location.pathname === '/coach/scheduling/setting' ||
      location.pathname === '/coach/scheduling/calendar' ||
      location.pathname === '/coach/scheduling/zoom'

    // If on a scheduling tab, no timezone is set, and modal was previously dismissed, show it again
    if (isSchedulingTab && !selectedTimezone && isModalDismissed) {
      setIsModalDismissed(false)
      setShowTimezoneModal(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, selectedTimezone, isLoadingTimezone])

  // Redirect to calendar tab if user tries to access disabled tabs
  useEffect(() => {
    if (isLoadingTimezone) {
      return
    }

    // If calendar is not connected and user is trying to access booking or setting tabs
    if (
      !isCalendarConnected &&
      selectedTimezone &&
      (location.pathname === '/coach/scheduling/booking' ||
        location.pathname === '/coach/scheduling/setting' ||
        location.pathname === '/coach/scheduling')
    ) {
      history.replace('/coach/scheduling/calendar')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    location.pathname,
    isCalendarConnected,
    selectedTimezone,
    isLoadingTimezone,
  ])

  // Refresh calendar status when window regains focus (after OAuth redirects)
  useEffect(() => {
    const handleFocus = () => {
      if (!isLoadingTimezone && selectedTimezone) {
        refreshCalendarStatus()
      }
    }

    window.addEventListener('focus', handleFocus)
    return () => {
      window.removeEventListener('focus', handleFocus)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoadingTimezone, selectedTimezone])

  // Refresh calendar connection status
  const refreshCalendarStatus = async () => {
    try {
      const { data } = await API.CoachAPI.coachUser.getTimezone()
      const calendarConnected = data?.isAbroadCalendarConnected || false
      setIsCalendarConnected(calendarConnected)
    } catch (error) {
      Error.showError(error)
    }
  }

  // Define pages for NavTabs
  const pages = useMemo(() => {
    return [
      {
        path: '/coach/scheduling/booking',
        name: 'Booking',
        defaultUrl: '/coach/scheduling',
        disabled: !isCalendarConnected,
      },
      {
        path: '/coach/scheduling/setting',
        name: 'Settings',
        disabled: !isCalendarConnected,
      },
      {
        path: '/coach/scheduling/calendar',
        name: 'Calendar',
        disabled: false,
      },
      {
        path: '/coach/scheduling/zoom',
        name: 'Zoom',
        disabled: false,
      },
    ]
  }, [isCalendarConnected])

  // Handle timezone save from modal
  const handleTimezoneSave = async (timezone) => {
    try {
      setIsSavingTimezone(true)
      await API.CoachAPI.coachUser.updateTimezone({
        timezone: timezone,
      })
      setSelectedTimezone(timezone)
      setShowTimezoneModal(false)
      Notification.showNotification('Timezone updated successfully.', 'success')
      // Refresh currently active tab data by remounting tab content
      setTabRefreshKey((prevKey) => prevKey + 1)
    } catch (error) {
      Error.showError(error)
    } finally {
      setIsSavingTimezone(false)
    }
  }

  // Handle timezone change from header
  const handleTimezoneChange = async (event) => {
    const nextTimezone = event?.target?.value || ''
    if (!nextTimezone || nextTimezone === selectedTimezone) {
      return
    }

    try {
      await API.CoachAPI.coachUser.updateTimezone({
        timezone: nextTimezone,
      })
      setSelectedTimezone(nextTimezone)
      Notification.showNotification('Timezone updated successfully.', 'success')
      // Refresh currently active tab data by remounting tab content
      setTabRefreshKey((prevKey) => prevKey + 1)
    } catch (error) {
      Error.showError(error)
    }
  }

  return (
    <>
      <Helmet>
        <title>{'Scheduling | Abroad'}</title>
        <meta name='title' content={'Scheduling | Abroad'}></meta>
        <meta property='og:title' content={'Scheduling | Abroad'}></meta>
        <meta property='twitter:title' content={'Scheduling | Abroad'}></meta>
        <link
          rel='canonical'
          href={`${process.env.REACT_APP_DOMAIN}/coach/scheduling`}></link>
        <meta
          property='og:url'
          content={`${process.env.REACT_APP_DOMAIN}/coach/scheduling`}></meta>
        <meta
          property='twitter:url'
          content={`${process.env.REACT_APP_DOMAIN}/coach/scheduling`}></meta>
      </Helmet>

      <PageHeader
        titleText='SCHEDULING'
        wrapperClassName='d-flex justify-content-between'
        extraFn={() => (
          <div className='d-flex align-items-center justify-content-end mt-3 mt-lg-0'>
            {!isLoadingTimezone && (
              <SelectionField
                options={timezoneOptions}
                getValueAsOption
                optionplaceholder='Select time zone'
                isUseFormikSelectionField={false}
                value={selectedTimezone}
                onChange={handleTimezoneChange}
                className='custom-timezone-select'
              />
            )}
          </div>
        )}
        titleClasses='mb-0 sentence-case-text'
        colClasses='px-0'
      />

      {/* Navigation Tabs */}
      <div className='custom-offset-lg-1 custom-col-lg-12 px-lg-0 pt-5'>
        <div className='d-flex justify-content-between mb-3'>
          <NavTabs
            pages={pages}
            NavLink={NavLink}
            pathname={location?.pathname}
            hash={location?.hash}
          />
        </div>

        {/* Tab Content */}
        {isLoadingTimezone ? (
          <div className='text-center py-5'>
            <ABSpinner />
          </div>
        ) : selectedTimezone ? (
          <Switch key={tabRefreshKey}>
            <Route
              exact
              path='/coach/scheduling'
              render={() => (
                <BookingTab
                  timezone={selectedTimezone}
                  isCalendarConnected={isCalendarConnected}
                />
              )}
            />
            <Route
              exact
              path='/coach/scheduling/booking'
              render={() => (
                <BookingTab
                  timezone={selectedTimezone}
                  isCalendarConnected={isCalendarConnected}
                />
              )}
            />
            <Route
              exact
              path='/coach/scheduling/setting'
              render={() => <SettingTab timezone={selectedTimezone} />}
            />
            <Route
              exact
              path='/coach/scheduling/calendar'
              render={() => (
                <CalendarTab
                  onRefreshCalendars={refreshCalendarStatus}
                  isCalendarConnected={isCalendarConnected}
                />
              )}
            />
            <Route
              exact
              path='/coach/scheduling/zoom'
              render={() => <ZoomTab />}
            />
          </Switch>
        ) : (
          <div className='text-center py-5'>
            <p>Please select a timezone to continue.</p>
          </div>
        )}
      </div>

      {/* Timezone Modal */}
      <TimezoneModal
        show={showTimezoneModal}
        setShowModal={(show) => {
          setShowTimezoneModal(show)
          if (!show) {
            setIsModalDismissed(true)
          }
        }}
        onSave={handleTimezoneSave}
        isSaving={isSavingTimezone}
      />
    </>
  )
}

export default Scheduling
